import { asTheme, type Theme } from '@ehr/shared';

/**
 * ehr#420 - the signed-in person's own theme (set on their own profile next to Time format), applied to the
 * whole app as `<html data-theme="light|dark">`. 'system' follows the device's light / dark setting and
 * switches with it. The session sets it from /auth/me.
 *
 * The last choice is also kept in this browser (THEME_KEY), only so the next load paints in the right theme
 * before /auth/me answers: index.html reads it before the app starts, so a dark page never flashes white.
 * The server's value always wins once it arrives.
 */
export const THEME_KEY = 'ehr.theme';

let chosen: Theme = 'light';
let media: MediaQueryList | null = null;

const deviceDark = (): boolean => !!media?.matches;

/** What the page shows now: 'dark' or 'light' ('system' resolved against the device). */
export const resolvedTheme = (): 'light' | 'dark' =>
  chosen === 'dark' || (chosen === 'system' && deviceDark()) ? 'dark' : 'light';

function paint(): void {
  const root = document.documentElement;
  const t = resolvedTheme();
  root.dataset.theme = t;
  root.style.colorScheme = t; // native controls (scrollbars, date pickers, selects) follow
}

/** Start: the browser's last choice, and the device's setting for 'system'. Call once before render. */
export function initTheme(): void {
  try {
    chosen = asTheme(localStorage.getItem(THEME_KEY));
  } catch {
    chosen = 'light';
  }
  try {
    media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', () => {
      if (chosen === 'system') paint();
    });
  } catch {
    media = null;
  }
  paint();
}

/** The person's theme from the server (or a change on their profile): apply it and remember it here. */
export function setPersonalTheme(t: unknown): void {
  chosen = asTheme(t);
  try {
    localStorage.setItem(THEME_KEY, chosen);
  } catch {
    /* private window or storage blocked: the page still switches */
  }
  paint();
}

export const personalTheme = (): Theme => chosen;
